import React, {useEffect, useRef, useState} from "react";
import {Alert, Button, Spinner} from "react-bootstrap";
import {changedOnText} from "./useRsoInputWindow";
import {readRsoSwitch, setRsoSwitch} from "./useRsoSwitch";
import RsoSignInExpired, {isSignInExpired} from "./RsoSignInExpired";
import "./RsoSwitchPanel.css";

// The top of the Admin page's "RSO" pop-up: is RSO on or off, and one button to flip it.
// ON = Sales see the RSO tile and can send; OFF = the tile is hidden and NANCY refuses every Sales
// RSO request. Only accounts NANCY says may manage it (canManage) get the button.
// onBusyChange(busy): a flip is running, so the pop-up stays open until NANCY answers.
// onChanged(value): the switch changed (the pop-up shows or hides the input-window section).
export default function RsoSwitchPanel({token, onBusyChange, onChanged}) {
  const [state, setState] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signInExpired, setSignInExpired] = useState(false);
  const [notice, setNotice] = useState("");
  const alive = useRef(true);
  useEffect(() => {alive.current = true; return () => {alive.current = false;};}, []);
  useEffect(() => {if (onBusyChange) onBusyChange(busy);}, [busy, onBusyChange]);

  useEffect(() => {
    if (!token) return undefined;
    let current = true;
    setState(null);setLoadError("");setSignInExpired(false);
    readRsoSwitch(token).then((value) => {
      if (!current) return;
      setState(value);
      if (onChanged) onChanged(value);
    }).catch((failure) => {
      if (!current) return;
      setSignInExpired(isSignInExpired(failure));
      setLoadError("Couldn't read whether RSO is on.");
    });
    return () => {current = false;};
  }, [token, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const flip = async () => {
    if (busy || !state) return;
    const next = !state.enabled;
    setBusy(true);setError("");setNotice("");setSignInExpired(false);
    try {
      const value = await setRsoSwitch(token, next);
      if (!alive.current) return;
      setState(value);
      setNotice(value.enabled ? "RSO is on. Sales now see the RSO tile and can send forecasts."
        : "RSO is off. Sales no longer see the RSO tile. Nothing they entered is lost.");
      if (onChanged) onChanged(value);
    } catch (failure) {
      if (!alive.current) return;
      if (isSignInExpired(failure)) setSignInExpired(true);
      else if (failure?.response?.status === 403) setError("Your account can't turn RSO on or off.");
      else setError(`RSO couldn't be turned ${next ? "on" : "off"}. Try again.`);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return <section className="rsw" aria-label="RSO on or off">
    {state && <p className={`rsw-state ${state.enabled ? "rsw-on" : "rsw-off"}`}>
      <strong>{`RSO is ${state.enabled ? "ON" : "OFF"}.`}</strong>{" "}
      {state.enabled ? "Sales see the RSO tile and can send forecasts and material changes."
        : "Sales see nothing and cannot send. Turn it on to go live."}
    </p>}
    {!state && !loadError && !signInExpired && <p className="rsw-note" role="status">Checking whether RSO is on…</p>}
    {state?.canManage && <Button type="button" className="rsw-flip" variant={state.enabled ? "outline-danger" : "success"}
      disabled={busy} onClick={flip}>
      {busy ? <><Spinner as="span" animation="border" size="sm" aria-hidden="true" /> Saving…</> : state.enabled ? "Turn RSO off" : "Turn RSO on"}
    </Button>}
    {state?.lastChange && <p className="rsw-note">
      Last changed {state.lastChange.changedByName ? `by ${state.lastChange.changedByName} ` : ""}on {changedOnText(state.lastChange.changedOnUtc)}.</p>}
    {signInExpired ? <RsoSignInExpired className="rsw-message" />
      : (error || (!state && loadError)) && <Alert variant="danger" className="rsw-message" role="alert">{error || loadError}
        {!state && loadError && <>{" "}<Button variant="link" size="sm" className="p-0 align-baseline"
          onClick={() => setAttempt((value) => value + 1)}>Try again</Button></>}</Alert>}
    {notice && <p className="rsw-notice" role="status">{notice}</p>}
  </section>;
}
